import React, { useEffect, useState } from 'react';
import { FiDownload, FiShare, FiX } from 'react-icons/fi';
import './InstallPrompt.css';

const DISMISS_KEY = 'pwaInstallDismissedAt';
const DISMISS_DAYS = 7;

function isStandalone() {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

function isIos() {
  const ua = window.navigator.userAgent || '';
  const iPadOs = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  return /iphone|ipad|ipod/i.test(ua) || iPadOs;
}

function recentlyDismissed() {
  const at = Number(localStorage.getItem(DISMISS_KEY) || 0);
  return at && Date.now() - at < DISMISS_DAYS * 24 * 60 * 60 * 1000;
}

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIosHint, setShowIosHint] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isStandalone() || recentlyDismissed()) return undefined;

    const onBeforeInstall = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
      setVisible(true);
    };
    const onInstalled = () => {
      setDeferredPrompt(null);
      setVisible(false);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);

    // iOS Safari has no install event; show manual "Add to Home Screen" steps instead.
    if (isIos()) {
      setShowIosHint(true);
      setVisible(true);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    if (outcome === 'accepted') setVisible(false);
    else dismiss();
  };

  if (!visible) return null;

  return (
    <div className="pwa-install" role="dialog" aria-label="Install Business OS">
      <img className="pwa-install__icon" src="/pwa-192.png" alt="" />
      <div className="pwa-install__text">
        <strong>Install Business OS</strong>
        {showIosHint && !deferredPrompt ? (
          <span>
            Tap <FiShare aria-label="Share" className="pwa-install__inline-icon" /> then
            {' '}<b>Add to Home Screen</b>.
          </span>
        ) : (
          <span>Open it like an app, full screen, right from your home screen or desktop.</span>
        )}
      </div>
      {deferredPrompt && (
        <button type="button" className="pwa-install__btn" onClick={install}>
          <FiDownload /> Install
        </button>
      )}
      <button
        type="button"
        className="pwa-install__close"
        onClick={dismiss}
        aria-label="Dismiss install prompt"
      >
        <FiX />
      </button>
    </div>
  );
}
